import { useState, useCallback, useEffect } from 'react';
import { useSocket } from '../context';
import { messagesService } from '../services';

/**
 * Custom hook for managing messages and conversations
 */
export const useMessages = () => {
  const { socket } = useSocket();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch all conversations
  const fetchConversations = useCallback(async (type = 'connection') => {
    setLoading(true);
    try {
      const data = await messagesService.getConversations(type);
      setConversations(data);
      return data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId) => {
    setLoading(true);
    try {
      const data = await messagesService.getMessages(conversationId);
      setMessages(data);
      return data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Send a message
  const sendMessage = useCallback(async (conversationId, content) => {
    try {
      const message = await messagesService.sendMessage(conversationId, content);
      setMessages((prev) => [...prev, message]);
      return message;
    } catch (error) {
      console.error('Error sending message:', error);
      return null;
    }
  }, []);

  // Mark messages as read
  const markAsRead = useCallback(async (conversationId) => {
    try {
      await messagesService.markAsRead(conversationId);
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
        )
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await messagesService.getUnreadCount();
      setUnreadCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Listen for new messages via socket
  useEffect(() => {
    if (socket) {
      socket.on('new_message', (message) => {
        // Add to messages if conversation is selected
        if (selectedConversation && message.conversation_id === selectedConversation.id) {
          setMessages((prev) => [...prev, message]);
        }

        // Update conversations list
        fetchConversations();
        fetchUnreadCount();
      });

      return () => {
        socket.off('new_message');
      };
    }
  }, [socket, selectedConversation, fetchConversations, fetchUnreadCount]);

  return {
    conversations,
    messages,
    selectedConversation,
    unreadCount,
    loading,
    fetchConversations,
    fetchMessages,
    sendMessage,
    markAsRead,
    fetchUnreadCount,
    setSelectedConversation,
  };
};

export default useMessages;
